import { useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ProductItemModel } from '../../../data-layer/posts/types';
import { removeProductFromCatalog } from '../../../data-layer/products/actions';
import { useDispatch } from '../../../data-layer/useDispatch';
import { useIsTabbarHidden } from '../../../hooks/useIsTabbarHidden';
import { CatalogHeader } from '../../../ui/business/CatalogHeader';
import { ProductCard } from '../../../ui/business/ProductCard/ProductCard';
import { PRODUCT_CARD_RATIO } from '../../../ui/business/ProductCard/constants';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { IconNewTab } from '../../../ui/icon/IconNewTab';
import { Button } from '../../../ui/input/Button';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { ResizableGrid } from '../../../ui/layout/ResizableGrid/ResizableGrid';
import { Typography } from '../../../ui/typography/Typography';
import { canEditBusiness, isBloggerBusiness } from '../../../utils/accessService';
import { analyticsService, EventSource } from '../../../utils/analyticsService';
import { useIsMobile } from '../../../utils/getIsMobile';
import {
    ymTrackProductCatalogClickout,
    ymTrackProductInCatalogClickout,
} from '../../../utils/metrika';
import { useTranslation } from '../../../utils/useTranslation';
import { useSearchAppMetricsAnalytics } from '../../hooks/useSearchAppMetricsAnalytics';
import { useCurrentUser } from '../../users/useCurrentUser';
import { useCatalog } from '../useCatalog';
import { useProductsCollection } from '../useProductsCollection';
import {
    BUSINESS_CATALOG_NAMESPACE_PREFIX,
    CARD_OFFSET,
    COLUMNS,
    CONTENT_HEIGHT,
    DOMAIN_HEIGHT,
    MOBILE_COLUMNS,
    PER_PAGE,
} from './constants';
import { useSearchAppAnalytics } from './hooks/useSearchAppAnalytics';

export const BusinessCatalog = () => {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const t = useTranslation();
    const currentUser = useCurrentUser();
    const { trackProductClick, trackButtonClick } = useSearchAppMetricsAnalytics({
        source: 'business_catalog_page',
    });
    const [query] = useSearchParams();
    const isFromSearchApp = query.get('morda_platform') === 'search_app';
    const params = useParams();
    const catalogId = params.catalogId!;
    const isTabbarHidden = useIsTabbarHidden();

    const { catalog, isLoading: catalogLoading } = useCatalog(catalogId);
    const namespace = `${BUSINESS_CATALOG_NAMESPACE_PREFIX}${catalogId}`;
    const { products, isInitialized, isEndReached, onLoadNext } = useProductsCollection({
        namespace,
        filters: { catalogId, amount: PER_PAGE },
    });
    const canRemoveProduct = canEditBusiness(catalog?.business?.id, currentUser);
    const showSiteButton = Boolean(
        catalog?.business?.siteUrl && !isBloggerBusiness(catalog.business?.type),
    );
    const isBlogger = isBloggerBusiness(catalog?.business?.type);

    const contentHeight = useMemo(() => {
        if (isBlogger) {
            return CONTENT_HEIGHT + DOMAIN_HEIGHT;
        }

        return CONTENT_HEIGHT;
    }, [isBlogger]);

    useSearchAppAnalytics({
        catalog,
        products,
        showSiteButton,
    });

    const onLoadNextProducts = useCallback(() => {
        if (!isEndReached) {
            onLoadNext({
                catalogId,
                amount: PER_PAGE,
            });
        }
    }, [isEndReached, catalogId, onLoadNext]);

    const onProductClick = useCallback(
        (product: ProductItemModel) => {
            analyticsService.trackClickOut({
                clickoutUrl: product.url,
                eventSource: EventSource.PRODUCTS,
                productId: product.id,
                businessId: catalog?.business?.id,
                target: 'product_in_catalog',
            });
            ymTrackProductInCatalogClickout({
                page: 'Каталог',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            isFromSearchApp && trackProductClick(product.id, product.url);
        },
        [catalog, currentUser, trackProductClick, isFromSearchApp],
    );

    const onProductCheckoutClick = useCallback(
        (product: ProductItemModel) => {
            analyticsService.trackClickOut({
                clickoutUrl: product.checkout?.url,
                eventSource: EventSource.PRODUCTS,
                productId: product.id,
                businessId: catalog?.business?.id,
                target: 'product_checkout',
            });
            ymTrackProductInCatalogClickout({
                page: 'Каталог',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            isFromSearchApp &&
                product.checkout?.url &&
                trackProductClick(product.id, product.checkout.url);
        },
        [catalog, currentUser, trackProductClick, isFromSearchApp],
    );

    const onCatalogClickOut = useCallback(() => {
        analyticsService.trackClickOut({
            clickoutUrl: catalog?.business?.siteUrl,
            eventSource: EventSource.PRODUCTS,
            businessId: catalog?.business?.id,
            target: 'product_catalog',
        });
        ymTrackProductCatalogClickout({
            page: 'Каталог',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        isFromSearchApp &&
            catalog?.business?.siteUrl &&
            trackButtonClick('GoToSite', catalog.business.siteUrl);
    }, [catalog, currentUser, isFromSearchApp, trackButtonClick]);

    const onProductRemove = useCallback(
        (productId: string) => dispatch(removeProductFromCatalog(namespace, productId, catalogId)),
        [catalogId, namespace, dispatch],
    );
    const getItemHeight = useCallback(
        (_index: number, width: number) => {
            const imageHeight = width / PRODUCT_CARD_RATIO;
            const cardHeight = imageHeight + contentHeight;

            return cardHeight - 2; // почему-то два лишних пикселя считается, отнимаю их вручную
        },
        [contentHeight],
    );

    if (!catalog?.business) {
        return null;
    }

    return (
        <DashboardPage hideHeader={true} overflow="hidden">
            <Flex width="100%" backgroundColor="var(--background-primary)" pb={2}>
                {catalog ? <CatalogHeader catalog={catalog} /> : null}
                {isInitialized && !catalogLoading && !products.length ? (
                    <Flex p={4} justifyContent="center">
                        <Typography variant="title2" weight="bold" pb={3}>
                            {t('В каталоге пока ничего нет, но вы можете найти интересное в ленте')}
                        </Typography>
                        <Button size="l" as={Link} to="/">
                            {t('Перейти в ленту')}
                        </Button>
                    </Flex>
                ) : (
                    <Flex width="100%" pl={2} pr={1}>
                        <ResizableGrid
                            itemsXMargin={CARD_OFFSET}
                            itemsYMargin={CARD_OFFSET}
                            bottomYMargin={isTabbarHidden ? 8 : undefined}
                            items={products}
                            loadMore={onLoadNextProducts}
                            getItemHeight={getItemHeight}
                            maxColumns={isMobile ? MOBILE_COLUMNS : COLUMNS}
                            minColumns={isMobile ? MOBILE_COLUMNS : COLUMNS}
                            renderItem={(index) => {
                                const product = products[index];
                                if (!product) {
                                    return null;
                                }

                                return (
                                    <ProductCard
                                        product={product}
                                        business={catalog?.business}
                                        contentHeight={contentHeight}
                                        canRemoveProduct={canRemoveProduct}
                                        onProductClick={() => onProductClick(product)}
                                        onCheckoutClick={() => onProductCheckoutClick(product)}
                                        onProductRemove={onProductRemove}
                                        fintechDiscountOptions={{ compact: true }}
                                    />
                                );
                            }}
                        />
                    </Flex>
                )}
                {showSiteButton ? (
                    <Box px={2} pt={2} width="100%">
                        <Button
                            as="a"
                            size="l"
                            variant="filled"
                            color="secondary"
                            textDecoration="none"
                            target="_blank"
                            href={catalog?.business?.siteUrl}
                            width="100%"
                            spacing={0.5}
                            onClick={onCatalogClickOut}
                        >
                            {catalog?.business?.shortSite}
                            <IconNewTab fontSize={14} color="inherit" />
                        </Button>
                    </Box>
                ) : null}
            </Flex>
        </DashboardPage>
    );
};
